'use client'

import { usePathname } from 'next/navigation'
import { useEffect } from 'react'

/** ヒーローは最初から見えているので対象外にする（PageHero / SolutionHero が付けている目印） */
const heroSelector = '[data-header-boundary]'
/** 画面下端から少し入ったところで出し始める */
const rootMargin = '0px 0px -10% 0px'
/** 同時に画面へ入った要素を、この間隔でずらして出す */
const STAGGER_MS = 140
const MAX_STAGGER_MS = 560
/** 1 要素だけを包む枠（max-w-* の中央寄せ用 div など）を、この深さまで降りて中身を対象にする */
const MAX_UNWRAP_DEPTH = 2
/** 同じタグ・同じ class の子がこの数以上並んでいれば、カードや FAQ の並びとみなす */
const MIN_REPEATED_ITEMS = 3

const unwrap = (element: Element) => {
  let container = element
  for (let depth = 0; depth < MAX_UNWRAP_DEPTH; depth++) {
    if (container.children.length !== 1) break
    container = container.children[0]
  }
  return container
}

/** カード・ステップ・FAQ などの並び。ul / ol か、同じ見た目の子が繰り返し並ぶ要素 */
const isList = (element: Element) => {
  if (element.tagName === 'UL' || element.tagName === 'OL') return true
  const items = Array.from(element.children)
  if (items.length < MIN_REPEATED_ITEMS) return false
  const [first] = items
  return items.every(
    (item) =>
      item.tagName === first.tagName && item.className === first.className
  )
}

/** SectionHeading の目印。見出しはフェードではなく、行ごとにせり上げる */
const headingSelector = '[data-section-heading]'

/** 見出しを含む要素は、見出しそのものが対象になるまで中へ降りる（外側ごとフェードさせない） */
const isolateHeading = (element: Element): Element[] =>
  !element.matches(headingSelector) && element.querySelector(headingSelector)
    ? Array.from(element.children).flatMap(isolateHeading)
    : [element]

/**
 * セクション内で個別にフェードさせる要素。
 * 1 つだけの子で包まれている間は中へ降り、並びは項目ごとに分けて、スクロールに合わせて 1 つずつ出す
 */
const revealTargets = (section: Element) =>
  Array.from(unwrap(section).children)
    .flatMap((child) => {
      const list = unwrap(child)
      return isList(list) ? Array.from(list.children) : [child]
    })
    .flatMap(isolateHeading)
    .filter((target): target is HTMLElement => target instanceof HTMLElement)

/**
 * main 直下のセクション（ヒーローを除く）の中身を、下へスクロールして画面に入るたびにフェードインさせる。
 * 背景は動かさず、中身だけを動かす。上へスクロールするときは動かさない。
 * マウント時点で既に画面内にある要素は隠さない（表示済みのものが一瞬消えるのを防ぐ）。
 * 描画は持たないので、ページのテンプレートのどこかに 1 つ置く
 */
export function SectionReveal() {
  const pathname = usePathname()

  // biome-ignore lint/correctness/useExhaustiveDependencies: 遷移のたびに監視対象を取り直す
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const sections = Array.from(
      document.querySelectorAll(`main section:not(${heroSelector})`)
    ).filter(
      (section) =>
        !section.parentElement?.closest('section') &&
        // ルートの layout も main で包んでおり、グローバルメニューの section まで拾うため、
        // いちばん内側の main（ページ本体）に属するものだけにする
        !section.closest('main')?.querySelector('main')
    )
    const targets = sections.flatMap(revealTargets)

    // 下へスクロールして画面に入った要素を出す
    const showObserver = new IntersectionObserver(
      (entries) => {
        const entering = entries
          .filter(
            (entry) =>
              entry.isIntersecting &&
              (entry.target as HTMLElement).dataset.reveal === 'hidden'
          )
          .map((entry) => entry.target as HTMLElement)
          .sort((a, b) =>
            a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
              ? -1
              : 1
          )
        entering.forEach((target, index) => {
          target.style.setProperty(
            '--reveal-delay',
            `${Math.min(index * STAGGER_MS, MAX_STAGGER_MS)}ms`
          )
          target.dataset.reveal = 'shown'
        })
      },
      { rootMargin }
    )

    // 上へスクロールして画面の下へ外れきった要素は隠し直し、次に下へスクロールしたときも同じ出方にする。
    // 画面の上へ抜けた要素は表示したままにし、上へスクロールするときは動かさない
    const hideObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const bottom = entry.rootBounds?.bottom ?? window.innerHeight
        if (entry.isIntersecting || entry.boundingClientRect.top < bottom)
          continue
        const target = entry.target as HTMLElement
        target.style.removeProperty('--reveal-delay')
        target.dataset.reveal = 'hidden'
      }
    })

    for (const target of targets) {
      // マウント時点で画面内にあるものは、一瞬消えないようそのまま見せる
      if (target.getBoundingClientRect().top >= window.innerHeight) {
        target.dataset.reveal = 'hidden'
      }
      showObserver.observe(target)
      hideObserver.observe(target)
    }

    // 表示し終えた要素から属性を外し、translate の containing block を残さない
    const onTransitionEnd = (event: TransitionEvent) => {
      const target = event.target as HTMLElement
      if (target.dataset.reveal !== 'shown') return
      delete target.dataset.reveal
      target.style.removeProperty('--reveal-delay')
    }
    document.addEventListener('transitionend', onTransitionEnd)

    return () => {
      showObserver.disconnect()
      hideObserver.disconnect()
      document.removeEventListener('transitionend', onTransitionEnd)
      for (const target of targets) {
        delete target.dataset.reveal
        target.style.removeProperty('--reveal-delay')
      }
    }
  }, [pathname])

  return null
}
